import {openDialog, useTranslationContext} from 'platform/components';

import {Preset} from '../../../../types/Api';
import {PresetApiStateEnum} from '../../constants/PresetApiStateEnum';
import {EditPresetDialog} from '../EditPresetDialog/EditPresetDialog';
import {PresetButtonActions} from '../PresetButtonActions/PresetButtonActions';

export type PresetActionsMenuProps = {
  preset: Preset;
  presetChanged: boolean;
  activePresetId: string | undefined;
  updatePreset: (presetId: string) => void;
  deletePreset: (presetId: string) => void;
  duplicatePreset: (presetId: string) => void;
  applyPresetToVirtual: (
    presetId: string,
    saveCurrentPreset?: boolean,
    onSuccess?: () => void
  ) => void;
  onCopySuccess?: () => void;
  applyToDefaultState?: PresetApiStateEnum;
  applyToDefaultDisabled?: boolean;
  resetOverrides: () => void;
  renamePreset: (presetId: string, name: string, desc: string, color?: string) => Promise<void>;
  /** data-testid forwarded to PresetButtonActions. */
  testId: string;
  /** data-testid set on the EditPresetDialog element. */
  editDialogTestId: string;
  /** data-testid passed through openDialog's options. */
  dialogOptionsTestId: string;
  /** openDialog id; omitted on the overflow path (preserves existing divergence). */
  dialogId?: string;
  /** Current preset color (passed to the color picker in the edit dialog). */
  presetColor?: string;
  /** Called when the user changes the preset color via the edit dialog. */
  onColorChange?: (presetId: string, color?: string) => void;
};

// Shared invocation of PresetButtonActions + its edit-preset dialog, used by both
// the visible strip and the overflow submenu. The two call sites have an existing
// cosmetic divergence in testids / openDialog id that specs assert — it is
// preserved here by parameterizing those strings (see *TestId / dialogId props).
export function PresetActionsMenu(props: PresetActionsMenuProps) {
  const t = useTranslationContext();

  return (
    <PresetButtonActions
      preset={props.preset}
      presetChanged={props.presetChanged}
      activePresetId={props.activePresetId}
      updatePreset={props.updatePreset}
      deletePreset={props.deletePreset}
      duplicatePreset={props.duplicatePreset}
      applyPresetToVirtual={props.applyPresetToVirtual}
      onCopySuccess={props.onCopySuccess}
      applyToDefaultDisabled={props.applyToDefaultDisabled}
      applyToDefaultState={props.applyToDefaultState}
      resetOverrides={props.resetOverrides}
      data-testid={props.testId}
      openEditModal={() =>
        openDialog(
          <EditPresetDialog
            presetToEdit={props.preset}
            initialColor={props.presetColor}
            data-testid={props.editDialogTestId}
            onColorPreview={(color) => props.onColorChange?.(props.preset.id, color)}
            onSubmit={(title, description, color) =>
              props.renamePreset(props.preset.id, title, description, color).finally(() => {
                props.onColorChange?.(props.preset.id);
              })
            }
          />,
          {
            ...(props.dialogId !== undefined ? {id: props.dialogId} : {}),
            title: t('general.presets.editPreset', {presetName: props.preset.title}),
            'data-testid': props.dialogOptionsTestId,
            size: 'small',
            scrollBehavior: 'outside',
          }
        )
      }
    />
  );
}
